import { Box, Stack, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { AppV1AppUserCredentialServiceDelete } from 'c1gen/permissions'
import { C1Button, C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1MoreButton } from 'components/core/button'
import { DeleteConfirmationDialog } from 'components/core/confirmation/dialog/delete'
import { C1CopyTextButton } from 'components/core/copytext/C1CopyTextButton'
import { DialogStyles } from 'components/core/dialog/styles'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list/EmptyList'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { QueryFilter } from 'components/core/search-filter/query'
import { ExpiresAtChip, RelativeTimestamp } from 'components/core/time/display'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { useAlert } from 'hooks/useAlert'
import { useTimedState } from 'hooks/useTimedState/useTimedState'
import * as jose from 'jose'
import { AppUserCredentialValue } from 'lib/actions/settings/vault'
import { useInitialFilters, useSearch } from 'lib/search'
import { Eye, EyeOff, Key } from 'lucide-react'
import {
    AppUserCredentialServiceSearchTermsRequest,
    AppUserCredentialServiceSearchTermsRequest_TermType as TermType,
    SearchAppUserCredentialsByVaultIDRequest,
} from 'pbts/c1/api/app/v1/app_user'
import { VaultOpenerServiceOpenRequest } from 'pbts/c1/api/vault/v1/vault'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    AppUserCredentialSearchTermsApi,
    deleteCredentialApi,
    searchAppUserCredentialsByVaultID,
} from 'redux_toolkit/app-user-credentials'
import { openVaultApi } from 'redux_toolkit/vaults'

const CredentialActions = ({ row }: { row: AppUserCredentialValue }): React.ReactElement => {
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const { mutate: deleteCredential } = deleteCredentialApi()
    const alert = useAlert()
    const intl = useIntl()
    return (
        <Stack direction="row" spacing={1}>
            <C1MoreButton
                options={[
                    {
                        label: <FormattedMessage id="app_user_credential_table_delete" defaultMessage="Delete" />,
                        permission: AppV1AppUserCredentialServiceDelete,
                        onClick: (): void => {
                            setDeleteConfirmationOpen(true)
                        },
                    },
                ]}
            />
            <DeleteConfirmationDialog
                open={deleteConfirmationOpen}
                itemName={intl.formatMessage({
                    id: 'delete_confirmation_credential_dialog_item_name',
                    defaultMessage: 'credential for {displayname}',
                }, {
                    displayname: row.appUser?.displayName || 'app user',
                })}
                onConfirm={(): void => {
                    setDeleteConfirmationOpen(false)
                    try {
                        deleteCredential({
                            appId: row.appId,
                            appUserId: row.appUserId,
                            credentialId: row.id,
                        }).then(() => {
                            alert(
                                <FormattedMessage
                                    id="delete_confirmation_credential_dialog_success"
                                    defaultMessage="Credential deleted successfully."
                                />,
                                'success',
                            )
                        })
                    } catch (err) {
                        alert(
                            <FormattedMessage
                                id="delete_confirmation_credential_dialog_error"
                                defaultMessage="Error deleting credential: {msg}"
                                values={{
                                    msg: (err as RpcError).message,
                                }}
                            />,
                            'error',
                        )
                    }
                }}
                onCancel={(): void => {
                    setDeleteConfirmationOpen(false)
                }}
                details={
                    <Box>
                        <Typography sx={DialogStyles.bold}>
                            <FormattedMessage
                                id="delete_confirmation_credential_dialog_warning"
                                defaultMessage="Deleting a credential does not deprovision the account."
                            />
                        </Typography>
                        <Typography>
                            <FormattedMessage
                                id="delete_confirmation_vault_dialog_description"
                                defaultMessage="Deleting permanently removes the credential from this vault. This action cannot be undone."
                            />
                        </Typography>
                    </Box>
                }
            />
        </Stack>
    )
}

const getColumns = (
    vaultId: string,
    keyPair?: jose.GenerateKeyPairResult,
    readerPublicKeyBytes?: Uint8Array,
): C1TableReduxColumn<AppUserCredentialValue>[] => [
    {
        field: 'account',
        width: '30%',
        label: <FormattedMessage
            id="app_user_credential_table_account"
            defaultMessage="Account"
        />,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => (
            <AppUserDisplayName appUser={row.appUser} margin={false} />
        ),
    },
    {
        field: 'application',
        label: <FormattedMessage
            id="app_user_credential_table_application"
            defaultMessage="Application"
        />,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => (
            row.app
                ? <AppDisplayName app={row.app} margin={false} />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'addedOn',
        label: <FormattedMessage
            id="app_user_credential_table_added_on"
            defaultMessage="Created on"
        />,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => (
            row.createdAt
                ? <RelativeTimestamp date={row.createdAt} />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'expiresAt',
        label: <FormattedMessage
            id="app_user_credential_table_expires_at"
            defaultMessage="Expires on"
        />,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => (
            row.expiresAt
                ? <ExpiresAtChip
                    timestamp={row.expiresAt}
                />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'password',
        label: <FormattedMessage
            id="app_user_credential_table_credential"
            defaultMessage="Password"
        />,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => {
            if (row.expiresAt && pbtsTimestamp.toDate(row.expiresAt) < new Date()) {
                return <C1EmptyIndicator />
            }

            return keyPair && readerPublicKeyBytes ? (
                <DecryptCredential
                    vaultId={vaultId}
                    appId={row.appId}
                    appUserId={row.appUserId}
                    credentialId={row.id}
                    keyPair={keyPair}
                    readerPublicKeyBytes={readerPublicKeyBytes}
                />
            ) : (
                <C1CircularProgress />
            )
        },
    },
    {
        field: 'actions',
        isSticky: () => true,
        shouldRender: (_, hasPermissions): boolean => hasPermissions(AppV1AppUserCredentialServiceDelete) || false,
        Component: ({ row }: { row: AppUserCredentialValue }): React.ReactElement => (
            <CredentialActions
                row={row}
            />
        ),
    },
]

export const AppUserCredentialsList: FC<{
    vaultId: string
    buttons?: React.ReactElement | React.ReactElement[]
}> = ({ vaultId, buttons }) => {
    const intl = useIntl()
    const [page, setPage] = useState<number>(0)
    const initialFilters = useInitialFilters(SearchAppUserCredentialsByVaultIDRequest)

    const emptyReq = useMemo<Partial<SearchAppUserCredentialsByVaultIDRequest>>(
        () => ({
            vaultId,
            expandMask: { paths: ['*'] },
        }),
        [vaultId],
    )

    const [filter, setFilter] = useState<SearchAppUserCredentialsByVaultIDRequest>(
        SearchAppUserCredentialsByVaultIDRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )
    const [keyPair, setKeyPair] = useState<jose.GenerateKeyPairResult>()
    const [readerPublicKeyBytes, setReaderPublicKeyBytes] = useState<Uint8Array>()
    const toast = useAlert()

    const generateInitialKeyPair = useCallback(async (): Promise<void> => {
        try {
            const generatedKeyPair = await jose.generateKeyPair('ECDH-ES', { extractable: true })
            setKeyPair(generatedKeyPair)
            const readerPublicKey = await jose.exportJWK(generatedKeyPair.publicKey)
            const jwkBytes = new TextEncoder().encode(JSON.stringify(readerPublicKey))
            setReaderPublicKeyBytes(new Uint8Array(jwkBytes))
        } catch (err) {
            console.error('Failed to generate initial key pair:', err)
            toast(
                <FormattedMessage
                    id="decrypt_credential_error"
                    defaultMessage="Error loading password {code}: {msg}"
                    values={{
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }
    }, [toast])

    useEffect(() => {
        generateInitialKeyPair()
    }, [generateInitialKeyPair])
    const {
        data,
        isError,
        isLoading,
        getPage,
        canLoadMore,
        error,
    } = searchAppUserCredentialsByVaultID.useSearchAppUserCredentialsByVaultID(
        SearchAppUserCredentialsByVaultIDRequest.create(filter),
        { notify: true, autoReload: true },
    )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const { canClear, clear, onChange, onQueryChange } = useSearch(
        SearchAppUserCredentialsByVaultIDRequest,
        filter,
        setFilter,
        emptyReq,
    )

    useEffect(() => {
        setFilter(SearchAppUserCredentialsByVaultIDRequest.create({
            ...emptyReq,
        }))
        setPage(0)
    }, [vaultId, emptyReq])

    const search = useMemo(
        () => (
            <Stack direction="row" spacing={2} alignItems="center">
                <QueryFilter
                    // autoFocus
                    query={filter?.query}
                    setQuery={(q): void => onQueryChange(q as unknown as string, true)}
                    onChange={onQueryChange}
                />
                <PluralFacetFilter<AppUserCredentialServiceSearchTermsRequest, 'appIds'>
                    onChange={onChange}
                    filter={filter}
                    term={TermType.APPS}
                    filterKey="appIds"
                    label={intl.formatMessage({ defaultMessage: 'App', id: 'app_cred_filter_app' })}
                    hook={AppUserCredentialSearchTermsApi.useLazyGetSearchTerms}
                    showCount
                />
                {canClear && (
                    <C1Button onClick={clear} variant="text" color="info">
                        <FormattedMessage id="components_clear_label" defaultMessage="Clear" />
                    </C1Button>
                )}
            </Stack>
        ),
        [filter, canClear, clear, onQueryChange, onChange, intl],
    )

    const cols = useMemo(() => getColumns(vaultId, keyPair, readerPublicKeyBytes), [vaultId, keyPair, readerPublicKeyBytes])
    const table = useMemo(
        () => (
            <C1TableRedux<AppUserCredentialValue>
                data={data}
                cols={cols}
                emptyState={
                    <EmptyList
                        text={
                            <FormattedMessage
                                id="components_admin_settings_app_user_credentials_empty"
                                defaultMessage="No credentials found"
                            />
                        }
                        icon={<C1Icon icon={Key} />}
                    />
                }
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, cols, data, error, isError, isLoading, onPageChange, page],
    )

    return (
        <Box sx={{ pt: 4 }}>
            <List<AppUserCredentialValue>
                table={table}
                buttons={buttons}
                search={search}
            />
        </Box>
    )
}

interface DecryptionHandlerProps {
    vaultId: string
    appId: string
    appUserId: string
    credentialId: string
    onDecrypted: (value: string) => void
    keyPair: jose.GenerateKeyPairResult
    readerPublicKeyBytes: Uint8Array
    setIsLoading: (isLoading: boolean) => void
}

const DecryptionHandler: FC<DecryptionHandlerProps> = ({
    vaultId,
    appId,
    appUserId,
    credentialId,
    onDecrypted,
    keyPair,
    readerPublicKeyBytes,
    setIsLoading,
}) => {
    const [attempted, setAttempted] = useState(false)
    const { data: rv, isLoading: isOpenLoading, error } = openVaultApi.useOpen(
        VaultOpenerServiceOpenRequest.create({
            vaultId,
            appId,
            appUserId,
            credentialId,
            readerPublicKey: readerPublicKeyBytes,
        }),
        {
            notify: true,
            actionable: !!readerPublicKeyBytes && !attempted, // Prevent infinite retries
        },
    )

    const decrypt = useCallback(async (): Promise<void> => {
        try {
            if (!rv || !keyPair) return

            const { plaintext } = await jose.compactDecrypt(rv.encryptedCredential, keyPair.privateKey, {
                keyManagementAlgorithms: ['ECDH-ES'],
                contentEncryptionAlgorithms: ['A128GCM'],
            })
            const decodedText = new TextDecoder().decode(plaintext)
            onDecrypted(decodedText)
        } catch (err) {
            console.error('Decryption failed:', err)
            setIsLoading(false)
        }
    }, [rv, keyPair, onDecrypted, setIsLoading])

    useEffect(() => {
        if (isOpenLoading) {
            setIsLoading(true)
            return
        }

        if (error) {
            setIsLoading(false)
            setAttempted(true) // Mark as attempted to stop retries
            return
        }

        if (rv === undefined && !isOpenLoading) {
            setIsLoading(false)
            setAttempted(true) // Stop retrying after first attempt
            return
        }

        if (rv) {
            decrypt()
        }
    }, [isOpenLoading, rv, error, setIsLoading, decrypt])
    if (error) {
        const err = (
            <FormattedMessage id="component_api_error_application" defaultMessage="Permission denied: user does not have permission to decrypt" />
        )
        return <MinimalAPIError err={err} />
    }

    return null
}

interface DecryptCredentialProps {
    vaultId: string
    appId: string
    appUserId: string
    credentialId: string
    keyPair: jose.GenerateKeyPairResult
    readerPublicKeyBytes: Uint8Array
}

export const DecryptCredential: FC<DecryptCredentialProps> = ({
    vaultId,
    appId,
    appUserId,
    credentialId,
    keyPair,
    readerPublicKeyBytes,
}) => {
    const intl = useIntl()
    const [showDecrypt, setShowDecrypt] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [decryptedValue, setDecryptedValue] = useState('')
    const [displayText, setDisplayText] = useState<Array<{ char: string; revealed: boolean }>>([])
    const scrambleChars = 'ΑΒΓΔΕΖΗΘΙΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζηθικλμνξοπρστυφχψω'
    const iterationsRef = useRef(0)
    const [decryptedTimeout, triggerDecryptedTimeout] = useTimedState(15000, false) // 15 seconds

    useEffect(() => {
        if (showDecrypt || isLoading) {
            iterationsRef.current = 0
            const targetLength = decryptedValue.length
            setDisplayText(
                Array.from({ length: 10 }, () => ({
                    char: scrambleChars[Math.floor(Math.random() * scrambleChars.length)],
                    revealed: false,
                })),
            )
            const scrambleInterval = setInterval(() => {
                setDisplayText((prev) =>
                    prev.map((_, i) => {
                        if (iterationsRef.current > i && decryptedValue) {
                            // Reveal actual character.
                            return { char: decryptedValue[i], revealed: true }
                        }
                        return {
                            char: scrambleChars[Math.floor(Math.random() * scrambleChars.length)],
                            revealed: false,
                        }
                    }))
                iterationsRef.current++
                if (iterationsRef.current > targetLength) {
                    clearInterval(scrambleInterval)
                }
            }, 100)
            return () => clearInterval(scrambleInterval)
        }
        return undefined
    }, [showDecrypt, decryptedValue, scrambleChars, isLoading])

    useEffect(() => {
        if (!decryptedTimeout) {
            setShowDecrypt(false)
        }
        if (!showDecrypt) {
            setDecryptedValue('')
            setDisplayText([])
            iterationsRef.current = 0
        }
    }, [decryptedTimeout, showDecrypt])

    const handleToggleVisibility = useCallback((): void => {
        setShowDecrypt((prev) => {
            const next = !prev
            if (next) {
                triggerDecryptedTimeout()
            }
            return next
        })
    }, [triggerDecryptedTimeout])

    const showDecryptedCredential = showDecrypt && decryptedTimeout

    const renderPassword = (): React.ReactElement => {
        if (!showDecryptedCredential) {
            return <Typography variant="monospace">•••••••••</Typography>
        }

        return (
            <Box>
                {displayText.map((item, idx) => (
                    <span
                        key={idx}
                        style={{
                            color: item.revealed ? 'var(--mui-palette-text-primary)' : 'var(--mui-palette-text-disabled)',
                            fontFamily: 'monospace',
                            transition: 'color 0.2s',
                        }}
                    >
                        {item.char}
                    </span>
                ))}
            </Box>
        )
    }
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 1 }}>
            <Typography variant="monospace" sx={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                {renderPassword()}
            </Typography>

            <C1IconButton
                onClick={handleToggleVisibility}
                size="small"
                aria-label={showDecryptedCredential
                    ? intl.formatMessage({
                        id: 'components_admin_settings_credentials_hide_password',
                        defaultMessage: 'Hide password',
                    })
                    : intl.formatMessage({
                        id: 'components_admin_settings_credentials_show_password',
                        defaultMessage: 'Show password',
                    })
                }
                aria-pressed={showDecryptedCredential}
            >
                {showDecryptedCredential ? <C1Icon icon={EyeOff} /> : <C1Icon icon={Eye} />}
            </C1IconButton>

            {!decryptedValue && showDecryptedCredential && (
                <DecryptionHandler
                    vaultId={vaultId}
                    appId={appId}
                    appUserId={appUserId}
                    credentialId={credentialId}
                    onDecrypted={setDecryptedValue}
                    keyPair={keyPair}
                    readerPublicKeyBytes={readerPublicKeyBytes}
                    setIsLoading={setIsLoading}
                />
            )}
            {decryptedValue && showDecryptedCredential && (
                <C1CopyTextButton
                    copyValue={decryptedValue}
                    tooltipText={intl.formatMessage({
                        id: 'components_admin_settings_app_user_credentials_copy_credential_tooltip',
                        defaultMessage: 'Copy credential',
                    })}
                />
            )}
        </Box>

    )
}
